import { Table, Switch, Dropdown, Menu, Input, Empty, Button, Radio } from "antd";
import Header from "../../../components/HeaderList";
import styles from "../styles/CondimentUI.module.css";
import { ReactComponent as MoreDetail } from "../../../assets/icons/more-detail.svg";
import { GripVertical } from "lucide-react";
import { useCallback, useMemo } from "react";

const CondimentUI = (props) => {
  const {
    activeType,
    dragOverId,
    filteredItems,
    items,
    loadingItems,
    loadingSummary,
    pathname,
    savingOrder,
    search,
    selectedGroup,
    summaryList,
    updatingStatusMap,
    onActionClick,
    onDragOverChange,
    onGroupSelect,
    onReorder,
    onRetry,
    onSearchChange,
    onStatusChange,
    onTypeChange,
  } = props;

  const statusColumns = useMemo(() => {
    const supportedModes = [
      { mode: "app", key: "status_app", title: "App" },
      { mode: "web", key: "status_web", title: "Web" },
      { mode: "uber", key: "status_uber", title: "Uber" },
      { mode: "kiosko", key: "status_kiosko", title: "Kiosko" },
    ];

    return supportedModes
      .filter(({ key }) =>
        items.some((item) => Object.prototype.hasOwnProperty.call(item, key))
      )
      .map(({ mode, key, title }) => ({
        title,
        dataIndex: key,
        align: "center",
        width: 110,
        render: (value, record) => {
          const requestKey = `${record.id}-${mode}`;
          return (
            <Switch
              checked={Boolean(Number(value))}
              disabled={Boolean(updatingStatusMap[requestKey])}
              loading={Boolean(updatingStatusMap[requestKey])}
              onChange={(checked) => onStatusChange(record.id, mode, checked)}
            />
          );
        },
      }));
  }, [items, onStatusChange, updatingStatusMap]);

  const getActionMenu = useCallback((record) => (
    <Menu>
      <Menu.Item
        key={`image-${record.id}`}
        onClick={() => onActionClick("Actualizar imagen")}
      >
        Actualizar Imagen
      </Menu.Item>
      <Menu.Item
        key={`price-${record.id}`}
        onClick={() => onActionClick("Actualizar precio")}
      >
        Actualizar Precio
      </Menu.Item>
    </Menu>
  ), [onActionClick]);

  const columns = useMemo(() => {
    return [
      {
        title: "",
        dataIndex: "drag",
        width: 52,
        align: "center",
        render: (_, record) => (
          <span
            className={styles.dragHandle}
            draggable={!savingOrder}
            onDragStart={(event) => {
              if (savingOrder) {
                event.preventDefault();
                return;
              }
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("text/plain", `${record.id}`);
            }}
          >
            <GripVertical size={16} />
          </span>
        ),
      },
      {
        title: "ID",
        dataIndex: "id",
        width: 110,
      },
      {
        title: "Nombre",
        dataIndex: "name",
      },
      {
        title: "Precio",
        dataIndex: "price",
        align: "center",
        width: 110,
        render: (value) => `$${Number(value || 0).toFixed(2)}`,
      },
      {
        title: "Orden",
        dataIndex: "setOrder",
        align: "center",
        width: 90,
      },
      ...statusColumns,
      {
        title: "",
        dataIndex: "actions",
        width: 64,
        align: "center",
        render: (_, record) => (
          <Dropdown overlay={getActionMenu(record)} trigger={["click"]}>
            <button
              type="button"
              className={styles.actionButton}
              aria-label={`Acciones de ${record.name}`}
            >
              <MoreDetail />
            </button>
          </Dropdown>
        ),
      },
    ];
  }, [getActionMenu, savingOrder, statusColumns]);

  return (
    <div className={styles.view}>
      <Header
        title="Condiments"
        back={pathname.includes("productos-por-sucursal") ? "/productos-por-sucursal" : "/productos"}
        actions={
          <Input.Search
            allowClear
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Buscar en el listado derecho"
            className={styles.search}
          />
        }
      />

      <div className={styles.layout}>
        <section className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <span className={styles.sidebarTitle}>Tipo</span>
            <Radio.Group
              value={activeType}
              onChange={(event) => onTypeChange(event.target.value)}
              className={styles.typeToggle}
              buttonStyle="solid"
            >
              <Radio.Button value="condiments">Condiments</Radio.Button>
              <Radio.Button value="setmenus">Setmenus</Radio.Button>
            </Radio.Group>
          </div>

          <div className={styles.groupList}>
            {summaryList.map((group) => {
              const isActive = selectedGroup?.id === group.id;
              return (
                <button
                  key={group.id}
                  type="button"
                  className={`${styles.groupCard} ${isActive ? styles.groupCardActive : ""}`}
                  onClick={() => onGroupSelect(group)}
                >
                  <span className={styles.groupName}>
                    {group.id} - {group.name}
                  </span>
                  <span className={styles.groupMeta}>
                    No. productos: {group.totalProducts}
                  </span>
                </button>
              );
            })}

            {!loadingSummary && summaryList.length === 0 && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={`No hay ${activeType} disponibles`}
              />
            )}
          </div>
        </section>

        <section className={styles.content}>
          <div className={styles.contentHeader}>
            <div>
              <span className={styles.contentTitle}>
                {selectedGroup
                  ? `${selectedGroup.id} - ${selectedGroup.name}`
                  : "Selecciona un registro"}
              </span>
              <span className={styles.contentSubtitle}>
                Arrastra desde el icono de la izquierda para reordenar. El orden
                se guarda al soltar el item.
              </span>
            </div>

            {selectedGroup && (
              <Button onClick={onRetry} disabled={loadingItems}>
                Recargar
              </Button>
            )}
          </div>

          <Table
            columns={columns}
            dataSource={filteredItems}
            rowKey="id"
            loading={loadingSummary || loadingItems || savingOrder}
            pagination={false}
            locale={{
              emptyText: selectedGroup
                ? "No hay items para mostrar"
                : "Selecciona un condimento o setmenu para ver sus items",
            }}
            onRow={(record) => ({
              className: dragOverId === record.id ? styles.dragOverRow : "",
              onDragOver: (event) => {
                event.preventDefault();
                if (savingOrder) {
                  return;
                }
                event.dataTransfer.dropEffect = "move";
                if (dragOverId !== record.id) {
                  onDragOverChange(record.id);
                }
              },
              onDragLeave: () => {
                if (dragOverId === record.id) {
                  onDragOverChange(null);
                }
              },
              onDrop: (event) => {
                event.preventDefault();
                if (savingOrder) {
                  return;
                }
                const draggedId = event.dataTransfer.getData("text/plain");
                onReorder(draggedId, record.id);
              },
            })}
          />
        </section>
      </div>
    </div>
  );
};

export default CondimentUI;
